import { useCallback } from 'react';

import { useLazyGetSessionQuery } from 'services/auth/auth';

import { setUser } from 'store/user/userSlice';

import { SESSION_KEYS } from 'utils/storage';
import { clearWorkOSCookies } from 'utils/workos';

import { useAppDispatch } from 'hooks';

/**
 * Provides a function that loads the WorkOS session from the backend,
 * dispatches user state, and stores the socket access token.
 *
 * Returns `true` if the session was loaded successfully, `false` otherwise.
 */
export const useWorkOSSession = () => {
  const dispatch = useAppDispatch();
  const [getSession] = useLazyGetSessionQuery();

  const loadSession = useCallback(async (): Promise<boolean> => {
    try {
      const sessionData = await getSession().unwrap();
      if (sessionData?.data?.authenticated) {
        dispatch(setUser(sessionData.data));
        if (sessionData.data.socketAccessToken) {
          sessionStorage.setItem(
            SESSION_KEYS.SOCKET_ACCESS_TOKEN,
            sessionData.data.socketAccessToken
          );
        }
        return true;
      }
    } catch {
      // Session invalid or expired
    }
    // Clear stale WorkOS markers so prepareHeaders doesn't skip the
    // Authorization header on subsequent JWT-based requests.
    clearWorkOSCookies();
    return false;
  }, [dispatch, getSession]);

  return { loadSession };
};
